import {
    Component,
    ElementRef,
    Input,
    ViewChild,
    HostListener,
    AfterViewInit,
    OnChanges,
    OnInit,
} from '@angular/core';
import {Device} from '@webmate-web-core/shared/types/devices/device';
import {DeviceService} from '@webmate-web-core/shared/services/device/device.service';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {PlatformType} from '@webmate-web-core/shared/types/platform';
import {
    faCode,
    faExpandArrowsAlt,
    faExclamationTriangle,
    faBolt,
    faInfoCircle,
    faBox,
} from '@fortawesome/pro-light-svg-icons';
import {IconDefinition} from '@fortawesome/fontawesome-svg-core';
import {WorkbenchConsoleService} from '../../../shared/services/workbench-console.service';
import {
    SeleniumCapabilityGeneratorComponent,
    SeleniumCapabilityGeneratorToolConfig
} from '@webmate-web-core/shared/tools/capability-generator/selenium-capability-generator.component';
import {ModalToolContainerService} from '@webmate-web-core/shared/tools/modaltoolcontainer/modaltoolcontainer.service';
import {WorkbenchContextService} from '../../../shared/services/workbench-context.service';
import {DeviceId, ProjectId} from '@webmate-web-core/shared/types/ids';
import {
    DeviceMalfunctionReporterComponent,
    DeviceMalfunctionReporterToolConfig
} from '@webmate-web-core/shared/tools/malfunctionreporter/device-malfunction-reporter.component';
import {ClipboardAccess} from '../services/wm-types.interface';
import {OpenDeviceDetailsEvent, InspectorEvent, ViewId} from '../../../shared/types';
import {InspectorService} from '../../../shared/services/inspector.service';
import {WorkbenchConsoleEvent} from '../consoles/workbench-console';
import {ConsoleToolbarSnapshotComponent} from './toolbar-actions/snapshot/console-toolbar-snapshot.component';
import {CdkDragDrop, moveItemInArray} from '@angular/cdk/drag-drop';
import {BrowserPersistenceService} from '@webmate-web-core/shared/services/persistence.service';
import {faPencil} from '@fortawesome/pro-solid-svg-icons';
import {distinctUntilChanged, take, map} from 'rxjs/operators';
import {WorkbenchLayoutManager} from '../../../shared/services/workbench-layout-manager.service';
import {BrowserSessionInfo} from '@webmate-web-core/shared/types/browsersession';
import {DevicePropertyNames} from '@webmate-web-core/shared/types/devices/device-properties';
import {FeatureService} from '@webmate-web-core/shared/services/feature.service';
import {Observable} from 'rxjs';
import {A11yToolsPlugin} from '../../a11y-tools/a11y-tools-plugin';

interface SpeedDialTool {
    label: string;
    faIcon: IconDefinition;
    supportedPlatforms: PlatformType[];
    command: () => void;
}

interface ToolbarTool  {
    type: string;
    supportedPlatforms: PlatformType[];
    conditional?: () => boolean;
}

interface ToolbarToolsOrder  {
    type: string;
    index: number
}

@UntilDestroy()
@Component({
    selector: 'wm-workbench-console-toolbar',
    templateUrl: 'console-toolbar.component.html',
    styleUrls: ['console-toolbar.component.less']
})
export class ConsoleToolbarComponent implements OnInit, OnChanges, AfterViewInit {

    @ViewChild(ConsoleToolbarSnapshotComponent) snapshotAction: ConsoleToolbarSnapshotComponent;

    @Input() viewId: ViewId;
    @Input() device: Device;
    projectId: ProjectId;

    protected readonly faPencil = faPencil;

    speedDialTools: SpeedDialTool[] =  [];
    isDialOpen = false;
    inspectorIsOpen: boolean = false;

    showOverflowLeft: boolean = false;
    showOverflowRight: boolean = false;

    allowAutomationButtonText: string = "";
    requestingAutomationChange: boolean = false;
    automationStateCache: string;

    isMobileDevice = false;
    isClipboardAvailable: boolean = false;
    isWindowsMachine: boolean = false;
    isLocalDE: boolean = false;
    isAndroidDevice: boolean = false;
    isIosDevice: boolean = false;
    deviceId: DeviceId;
    showDeviceDetails: boolean = false;
    toolbarTools: ToolbarTool[] =  [];
    toolbarToolsOrder: ToolbarToolsOrder[] =  [];
    editToolbarOrder: boolean = false;
    isHardwareScannerAvailable: boolean = false;
    isDevToolsIntegrationAvailable: boolean = false;
    isBandwidthLimitationAvailable: boolean = false;

    @HostListener('document:keydown.escape', ['$event'])
    onEscPressed(event: KeyboardEvent) {
        if (this.editToolbarOrder) {
            this.editToolbarOrder = false;
            event.preventDefault();
        }
    }

    activeExpedition?: BrowserSessionInfo;


    allAvailableSpeedDialTools: SpeedDialTool[] = [
        {
            label: 'Make Fullscreen',
            faIcon: faExpandArrowsAlt,
            supportedPlatforms: ["Android", "iOS", "MACOS", "WINDOWS"],
            command: () => {
                this.workbenchConsoleService.raiseWorkbenchConsoleEvent({type: 'fullscreen', targetDevice: this.device.id});
            }
        },
        {
            label: 'Ctrl + Alt + Del',
            faIcon: faBolt,
            supportedPlatforms: ["WINDOWS"],
            command: () => {
                this.workbenchConsoleService.raiseWorkbenchConsoleEvent({type: 'send-ctrl-alt-del', targetDevice: this.device.id});
            }
        },
        {
            label: 'Install App',
            faIcon: faBox,
            supportedPlatforms: ["Android", "iOS"],
            command: () => {
                this.workbenchConsoleService.raiseWorkbenchConsoleEvent({type: 'install-app', targetDevice: this.device.id, payload: {deviceId: this.device.id, projectId: this.projectId}});
            }
        },
        {
            label: 'Show Selenium Capabilities',
            faIcon: faCode,
            supportedPlatforms: ["Android", "iOS", "MACOS", "WINDOWS"],
            command: () => {
                this.modalToolService.openTool(SeleniumCapabilityGeneratorComponent, new SeleniumCapabilityGeneratorToolConfig(this.projectId, this.device));
            }
        },
        {
            label: 'Report Malfunction',
            faIcon: faExclamationTriangle,
            supportedPlatforms: ["Android", "iOS", "MACOS", "WINDOWS"],
            command: () => {
                this.modalToolService.openTool(DeviceMalfunctionReporterComponent, new DeviceMalfunctionReporterToolConfig(this.device.id, this.projectId));
            }
        },
        {
            label: 'Device Details',
            faIcon: faInfoCircle,
            supportedPlatforms: ["Android", "iOS", "MACOS", "WINDOWS"],
            command: () => {
                if (!this.showDeviceDetails) {
                    this.raiseOpenDeviceDetailsEvent(this.deviceId);
                } else {
                    this.raiseCloseDeviceDetailsEvent();
                }
            }
        },
        {
            label: 'Edit Toolbar Order',
            faIcon: faPencil,
            supportedPlatforms: ["Android", "iOS", "MACOS", "WINDOWS"],
            command: () => {
                this.editToolbarOrder = !this.editToolbarOrder;
            }
        },
    ];

    /** Tool types shown for headless consoles, regardless of the (unreliable) backend-reported platform.
     *  Only tools that work without the console event bus and without a <canvas> belong here. */
    private readonly headlessToolTypes = ['Snapshot', 'Bandwidth'];

    /** Speed-dial entries shown for headless consoles, selected by label for the same reason as
     *  {@link headlessToolTypes}: the backend-reported platform cannot be used to filter them. */
    private readonly headlessSpeedDialLabels = [
        'Make Fullscreen',
        'Edit Toolbar Order'
    ];

    private allAvailableToolbarTools: ToolbarTool[] = [
        {
            type: 'Rotation',
            supportedPlatforms: ["Android", "iOS"],
        },
        {
            type: 'Snapshot',
            supportedPlatforms: ["Android", "iOS", "MACOS", "WINDOWS"],
        },
        {
            type: 'DevTools',
            supportedPlatforms: ['Android', 'iOS'],
            conditional: () => this.isDevToolsIntegrationAvailable,
        },
        {
            type: 'Bandwidth',
            supportedPlatforms: ["Android", "iOS", "MACOS", "WINDOWS"],
            conditional: () => this.isBandwidthLimitationAvailable,
        },
        {
            type: 'Clipboard',
            supportedPlatforms: ["Android", "iOS", "MACOS", "WINDOWS"],
            conditional: () => this.isClipboardAvailable,
        },
        {
            type: 'Simulation',
            supportedPlatforms: ["Android", "iOS"],
        },
        {
            type: 'Streaming Quality',
            supportedPlatforms: ["Android", "iOS", "MACOS"],
        },
        {
            type: 'Special Keys',
            supportedPlatforms: ["WINDOWS"],
            conditional: () => this.isLocalDE,
        },
        {
            type: 'Allow Automation',
            supportedPlatforms: ["Android", "iOS", "MACOS", "WINDOWS"],
        },
        {
            type: 'Remove Annotations',
            supportedPlatforms: ["MACOS", "WINDOWS"],
            conditional: () => this.isAnnotationProducingA11yViewAvailable() && !!this.activeExpedition,
        },
        {
            type: 'Gesture Metrics',
            supportedPlatforms: ["iOS"],
        },
        {
            type: 'Screen Reader',
            supportedPlatforms: ["Android"],
            conditional: () =>
                this.device.properties?.['streaming.audio.available'] &&
                +this.device.platform.platformVersion.split('.')[0] >= 13,
        },
        {
            type: 'Interaction Mode',
            supportedPlatforms: ["iOS"],
            conditional: () => this.device.properties[DevicePropertyNames.IOSInteractionType] === 'controller',
        },
        {
            type: 'Hardware Scanner Mode',
            supportedPlatforms: ["Android"],
            conditional: () => this.isHardwareScannerAvailable,
        },
        {
            type: 'Browser Session',
            supportedPlatforms: ["MACOS", "WINDOWS"],
        },
        {
            type: 'Appium Inspector',
            supportedPlatforms: ["Android", "iOS"],
            conditional: () => this.device.properties[DevicePropertyNames.AutomationAvailable] === true,
        }
    ];

    constructor(private deviceService: DeviceService,
                private workbenchContextService: WorkbenchContextService,
                private workbenchConsoleService: WorkbenchConsoleService,
                private modalToolService: ModalToolContainerService,
                private featureService: FeatureService,
                private readonly inspectorService: InspectorService,
                private browserPersistenceService: BrowserPersistenceService,
                private layoutManager: WorkbenchLayoutManager) {}

    ngOnInit() {
        this.workbenchContextService.subscribeWorkbenchContext$().subscribe(context => {
            if (context.expeditionContext?.activeExpedition !== this.activeExpedition) {
                this.activeExpedition = context.expeditionContext?.activeExpedition;
            }
            this.projectId = context.project;
            if (context.deviceContext?.activeDevice) {
                this.deviceId = context.deviceContext?.activeDevice;
            }
            if (context.deviceContext?.activeDevice !== this.deviceId) {
                this.editToolbarOrder = false; // reset on deviceId change
            }
            this.buildTools();
            this.orderTools();
        });

        this.layoutManager.subscribeActiveViewId$().pipe(
            untilDestroyed(this), distinctUntilChanged()
        ).subscribe(activeView => {
            if (activeView != this.viewId && this.inspectorIsOpen){
                this.raiseCloseDeviceDetailsEvent()
                this.inspectorIsOpen = false;
            }
        });
    }

    ngOnChanges() {
        this.updateAutomationButtonState(this.device);
        this.isMobileDevice = ['Android', 'iOS'].includes(this.device.platform.platformType!);
        this.isClipboardAvailable = this.hasClipboardAccess(this.device);
        this.buildSpeedDialTools();
        this.isWindowsMachine = this.device.properties['machine.platform.type'] === 'WINDOWS';
        this.isLocalDE = this.device.properties['os.locale'] === 'de_DE';
        this.isAndroidDevice = this.device.platform.platformType === 'Android';
        this.isIosDevice = this.device.platform.platformType === 'iOS';
        this.isHardwareScannerAvailable = this.hasHardwareScannerAccess(this.device);
        this.checkDevToolsIntegrationAvailability(this.device).subscribe(isAvailable => {
            this.isDevToolsIntegrationAvailable = isAvailable;
        });
        this.checkBandwithLimitAvailibility().subscribe(isAvailable => {
            this.isBandwidthLimitationAvailable = isAvailable;
        });

        this.buildTools();
        this.orderTools();
    }

    ngAfterViewInit() {
        const nativeActionsElement = this.mainActionsContainer.nativeElement;

        setTimeout(() => {
            this.showOverflowRight = nativeActionsElement.scrollWidth > nativeActionsElement.clientWidth;
        });

        nativeActionsElement.addEventListener('wheel', (e: any) => {
            // Prevent default vertical scroll
            e.preventDefault();

            const scrollingHorizontal = Math.abs(e.deltaY) < Math.abs(e.deltaX);
            const equal = Math.abs(e.deltaY) === Math.abs(e.deltaX);
            if (!equal) {
                const delta = scrollingHorizontal ? e.deltaX : e.deltaY;
                const scrollValue = Math.sign(delta) * 20;
                nativeActionsElement.scrollLeft += scrollValue;
            }
        }, { passive: false });

        nativeActionsElement.addEventListener('scroll', (e: any) => {
            this.showOverflowLeft = nativeActionsElement.scrollLeft > 0;
            this.showOverflowRight = nativeActionsElement.scrollLeft + nativeActionsElement.clientWidth < nativeActionsElement.scrollWidth - 1;
        });
    }

    public handleConsoleEvent(event: WorkbenchConsoleEvent) {
        switch (event.type) {
            case 'toolbar:openScreenshot': {
                this.snapshotAction.open('screenshot', event.payload['findingId']);
                return;
            }
            case 'toolbar:openVideo': {
                this.snapshotAction.open('video', event.payload['findingId']);
                return;
            }
        }
    }

    handleChangeAutomation = () => {
        this.automationStateCache = this.device.properties[DevicePropertyNames.AutomationEnabled];
        this.requestingAutomationChange = true;
        this.updateAutomationButtonState(this.device);
        this.deviceService.requestVisibilityToJobsChange$(this.device.id, !this.device.isAvailableToJobs()).subscribe({
            error: () => {
                this.requestingAutomationChange = false;
                this.automationStateCache = this.device.properties[DevicePropertyNames.AutomationEnabled];
                this.updateAutomationButtonState(this.device);
            }
        });
    }

    activateHardwareScanner() {
        this.workbenchConsoleService.raiseWorkbenchConsoleEvent({type: 'activate-hardware-scanner', targetDevice: this.deviceId});
    }

    updateAutomationButtonState(device: Device) {
        if (this.automationStateCache != this.device.properties[DevicePropertyNames.AutomationEnabled]) {
            this.requestingAutomationChange = false;
        }
        if (this.requestingAutomationChange) {
            this.allowAutomationButtonText = "Requesting";
        } else {
            if (device.isAvailableToJobs()) {
                this.allowAutomationButtonText = "Block Automation";
            } else {
                this.allowAutomationButtonText = "Allow Automation";
            }
        }
    }

    showSpeedDial = () => {
        this.isDialOpen = true;
    }

    hideSpeedDial = () => {
        this.isDialOpen = false;
    }

    @ViewChild('mainactionscontainer') mainActionsContainer: ElementRef;

    hasClipboardAccess(device: Device) {
        return !!device.properties[ClipboardAccess];
    }

    hasHardwareScannerAccess(device: Device) {
        return device.properties[DevicePropertyNames.MachineId] === '22329522506979';
    }

    checkDevToolsIntegrationAvailability(device: Device): Observable<boolean> {
        // true if devtools are available on device and the feature 'webmate.frontend.devtools-integration' is active
        return this.featureService.getFeature('webmate.frontend.devtools-integration').pipe(
            take(1),
            map(feature => !!feature && feature.isActive && device.properties['machine.devtools.available'] === true)
        )
   }

    private checkBandwithLimitAvailibility(): Observable<boolean> {
        return this.featureService.getFeature('webmate.network-simulation').pipe(
            take(1),
            map(feature => !!feature && feature.isActive)
        )
    }

    raiseOpenDeviceDetailsEvent(deviceId: string) {
        this.inspectorService.changeItem('DeviceDetails');
        const event: OpenDeviceDetailsEvent = { type: 'OpenDeviceDetails', targetDevice: deviceId };
        this.inspectorService.raiseInspectorEvent(event);
        this.showDeviceDetails = true;
        this.inspectorIsOpen = true;
    }

    raiseCloseDeviceDetailsEvent() {
        const event: InspectorEvent ={type: "ClearInspector"};
        this.inspectorService.raiseInspectorEvent(event);
        this.showDeviceDetails = false;
    }

    onToolbarToolDrop(event: CdkDragDrop<ToolbarTool[]>) {
        moveItemInArray(this.toolbarTools, event.previousIndex, event.currentIndex);
        this.persistToolOrder();
    }

    persistToolOrder() {
        this.toolbarToolsOrder = this.toolbarTools.map((tool, index) => ({
            type: tool.type,
            index
        }));
        this.browserPersistenceService.setObjectItem(
            `tool-order-${this.device.properties['machine.platform.type']}`,
            this.toolbarToolsOrder
        );
    }

    buildSpeedDialTools() {
        if (this.device.isHeadlessDevice()) {
            this.speedDialTools = this.allAvailableSpeedDialTools.filter(tool =>
                this.headlessSpeedDialLabels.includes(tool.label));
            return;
        }
        this.speedDialTools = this.allAvailableSpeedDialTools.filter(tool =>
            tool.supportedPlatforms.includes(this.device.platform.platformType as PlatformType));
    }

    buildTools() {
        if (this.device.isHeadlessDevice()) {
            // Headless input bypasses the console event bus and the console renders a <video>, not a
            // <canvas>; only an explicit allow-list of compatible tools applies, independent of the
            // backend-reported platform (which is unreliable for headless devices).
            this.toolbarTools = this.allAvailableToolbarTools.filter(tool =>
                this.headlessToolTypes.includes(tool.type) &&
                (tool.conditional === undefined || tool.conditional())
            );
            return;
        }
        this.toolbarTools = this.allAvailableToolbarTools.filter(tool =>
            tool.supportedPlatforms.includes(this.device.platform.platformType as PlatformType) &&
            (tool.conditional === undefined || tool.conditional())
        );
    }

    private isAnnotationProducingA11yViewAvailable(): boolean {
        return [
            A11yToolsPlugin.InteractivePageToolsViewId,
            A11yToolsPlugin.WCAGAnalysisViewId,
            A11yToolsPlugin.TabOrderAnalysisViewId
        ].some(viewId => this.layoutManager.isViewRegistered(viewId));
    }

    orderTools() {
        this.toolbarToolsOrder = this.browserPersistenceService.getObjectItem(`tool-order-${this.device.properties['machine.platform.type']}`) as ToolbarToolsOrder[];
        if (this.toolbarToolsOrder) {
            this.toolbarTools.sort((a, b) => {
                const orderA = this.toolbarToolsOrder.find(t => t.type === a.type)?.index ?? Number.MAX_SAFE_INTEGER;
                const orderB = this.toolbarToolsOrder.find(t => t.type === b.type)?.index ?? Number.MAX_SAFE_INTEGER;
                return orderA - orderB;
            });
        }
    }
}
